import { UserFeatureFlag } from '@@types/api.types';
import { successToast } from '@hooks/toast.hook';
import {
  useAdminUpdateUserFeatureFlags,
  useApproveUser,
  useBlockUser,
  useMarkUserAsVerified,
  useResendVerificationEmail,
  useSetAccessWindow,
  useUnapproveUser,
  useUnblockUser,
} from '@queries/admin-users.query';
import { sanitizeUserFeatureFlags } from '@utils/feature-flag.util';
import { useCallback, useState } from 'react';

export type AdminUsersActions = {
  handleApproveToggle: (userId: string, shouldApprove: boolean) => Promise<void>;
  handleBlockToggle: (userId: string, shouldBlock: boolean, reason?: string) => Promise<void>;
  handleMarkAsVerified: (userId: string) => Promise<void>;
  handleResendVerification: (userId: string) => Promise<void>;
  handleSetAccessWindow: (userId: string, accessExpiredAt: string | null) => Promise<void>;
  handleUpdateUserFeatureFlags: (userId: string, featureFlags: UserFeatureFlag[]) => Promise<void>;
  loadingUserIds: Record<string, boolean>;
};

export const useAdminUsersActions = (): AdminUsersActions => {
  const approveUser = useApproveUser();
  const blockUser = useBlockUser();
  const markAsVerified = useMarkUserAsVerified();
  const resendVerification = useResendVerificationEmail();
  const setAccessWindow = useSetAccessWindow();
  const unapproveUser = useUnapproveUser();
  const unblockUser = useUnblockUser();
  const updateUserFeatureFlags = useAdminUpdateUserFeatureFlags();

  const [loadingUserIds, setLoadingUserIds] = useState<Record<string, boolean>>({});

  const runWithLoading = useCallback(async (userId: string, action: () => Promise<void>) => {
    setLoadingUserIds((prev) => ({ ...prev, [userId]: true }));
    try {
      await action();
    } finally {
      setLoadingUserIds((prev) => {
        const { [userId]: _, ...rest } = prev;
        return rest;
      });
    }
  }, []);

  const handleApproveToggle = useCallback(
    async (userId: string, shouldApprove: boolean) => {
      await runWithLoading(userId, async () => {
        if (shouldApprove) {
          await approveUser.mutateAsync(userId);
          successToast({ description: 'User approved successfully.' });
        } else {
          await unapproveUser.mutateAsync(userId);
          successToast({ description: 'User approval revoked.' });
        }
      });
    },
    [approveUser, runWithLoading, unapproveUser],
  );

  const handleBlockToggle = useCallback(
    async (userId: string, shouldBlock: boolean, reason?: string) => {
      await runWithLoading(userId, async () => {
        if (shouldBlock) {
          await blockUser.mutateAsync({ id: userId, reasonBlocked: reason });
          successToast({ description: reason ? `User blocked: ${reason}` : 'User blocked.' });
        } else {
          await unblockUser.mutateAsync(userId);
          successToast({ description: 'User unblocked.' });
        }
      });
    },
    [blockUser, runWithLoading, unblockUser],
  );

  const handleResendVerification = useCallback(
    async (userId: string) => {
      await runWithLoading(userId, async () => {
        await resendVerification.mutateAsync(userId);
        successToast({ description: 'Verification email sent.' });
      });
    },
    [resendVerification, runWithLoading],
  );

  const handleMarkAsVerified = useCallback(
    async (userId: string) => {
      await runWithLoading(userId, async () => {
        await markAsVerified.mutateAsync(userId);
        successToast({ description: 'User marked as verified.' });
      });
    },
    [markAsVerified, runWithLoading],
  );

  const handleSetAccessWindow = useCallback(
    async (userId: string, accessExpiredAt: string | null) => {
      await runWithLoading(userId, async () => {
        await setAccessWindow.mutateAsync({ id: userId, data: { accessExpiredAt } });
        successToast({
          description: accessExpiredAt ? 'Access window set successfully.' : 'Access window removed.',
        });
      });
    },
    [runWithLoading, setAccessWindow],
  );

  const handleUpdateUserFeatureFlags = useCallback(
    async (userId: string, featureFlags: UserFeatureFlag[]) => {
      await runWithLoading(userId, async () => {
        await updateUserFeatureFlags.mutateAsync({
          data: { featureFlags: sanitizeUserFeatureFlags(featureFlags) },
          id: userId,
        });
        successToast({ description: 'User feature flags updated successfully.' });
      });
    },
    [runWithLoading, updateUserFeatureFlags],
  );

  return {
    handleApproveToggle,
    handleBlockToggle,
    handleMarkAsVerified,
    handleResendVerification,
    handleSetAccessWindow,
    handleUpdateUserFeatureFlags,
    loadingUserIds,
  };
};
